import React, { useState, useEffect } from "react";
import { Card, CardHeader, CardContent } from "../../Components/ui/card";
import { Button } from "../../Components/ui/button";
import { Input } from "../../Components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../Components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "../../Components/ui/dialog";
import { Label } from "../../Components/ui/label";
import axios from "axios";
import { useToast } from "../../Components/ui/use-toast";
import { Toaster } from "../../Components/ui/toaster";
import CircularProgress from "@mui/material/CircularProgress/CircularProgress";
import { RootState } from "../../State/store";
import { useSelector } from "react-redux";
import { convertUTCToLocal } from "../../../src/utils/convertUTCToLocal";
import { DotsHorizontalIcon } from "@radix-ui/react-icons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../Components/ui/dropdown-menu";
import { ContactlessOutlined } from "@mui/icons-material";

interface OperatorDomain {
  domain_id: number;
  domain_name: string;
  created_date: string;
}

const OperatorDomains: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [domains, setDomains] = useState<OperatorDomain[]>([]);
  const [domainName, setDomainName] = useState("");
  const [domainId, setDomainId] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isDeleteMode, setIsDeleteMode] = useState<boolean>(false);

  const [domainError, setDomainError] = useState<string>("");

  const adminUrl = useSelector(
    (state: RootState) => state.authentication.adminUrl
  );

  const workspaceTimezone = useSelector(
    (state: RootState) => state.authentication.workspaceTimezone
  );
    const accountId = useSelector((state: RootState) => state.authentication.account_id);
  const workspaceId = useSelector((state: RootState) => state.authentication.workspace_id);

  const toast = useToast();

  const domainRegex = /^(?!:\/\/)([a-zA-Z0-9-_]+\.)+[a-zA-Z]{2,}$/;

  const handleDomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    setDomainName(value);

    if (!value) {
      setDomainError("Please enter a domain name.");
    } else if (!domainRegex.test(value)) {
      setDomainError("Please enter a valid domain name (e.g., example.com).");
    } else {
      setDomainError(""); // Clear error when valid
    }
  };

  // Fetch the list of operator domains
  const fetchDomains = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${adminUrl}/GetOperatorEmailDomains`);
      if (response.data.status === "Success") {
        setDomains(response.data.operatorDomains);
      } else {
        toast.toast({
          title: "Error",
          description: "Unable to fetch operator domains.",
        });
      }
    } catch (error) {
      toast.toast({
        title: "Error",
        description: "Error occurred while fetching operator domains.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDomains();
  }, [adminUrl]);

  useEffect(() => {
    if (!open) {
      setIsEditMode(false);
      setIsDeleteMode(false);
      setTimeout(() => {
        document.body.style.pointerEvents = "";
      }, 500);
    }
  }, [open]);

  const handleCloseDialog = () => {
    setOpen(false);
    setDomainName("");
    setDomainError("");
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${date
      .toLocaleDateString("en-GB")
      .replace(/\//g, "-")} ${date.toLocaleTimeString("en-GB", {
      hour12: false,
    })}`;
  };

  const handleSubmit = () => {
    if (isDeleteMode) {
      deleteDomain();
    } else if (isEditMode) {
      editDomain();
    } else {
      handleAddDomain();
    }
  };

  const deleteDomain = async () => {
    try {
      setIsLoading(true);
      const response = await axios.delete(
        `${adminUrl}/DeleteOperatorDomainById?DomainId=${domainId}&DomainName=${domainName}&WorkspaceId=${workspaceId}&AccountId=${accountId}`
      );
      if (response.data.status === "Success") {
        toast.toast({
          title: "Success",
          description: "Operator Domain deleted successfully.",
        });
        await fetchDomains(); // Refresh domain list
      } else {
        toast.toast({
          title: "Error",
          description: "Failed to delete Operator Domain",
        });
      }
    } catch (error) {
      toast.toast({
        title: "Error",
        description: "Error occurred while delete operator domain.",
      });
    } finally {
      setOpen(false); // Close dialog
      setDomainName(""); // Reset input field
      setDomainId(0);
      setIsLoading(false);
    }
  };

  const editDomain = async () => {
    if (!domainName) {
      setDomainError("Please enter a domain name.");
      return;
    }

    if (!domainRegex.test(domainName)) {
      setDomainError("Please enter a valid domain name.");
      return;
    }
    try {
      setIsLoading(true);
      const response = await axios.put(
        `${adminUrl}/UpdateOperatorDomainById?DomainId=${domainId}&DomainName=${domainName}&WorkspaceId=${workspaceId}&AccountId=${accountId}`
      );
      if (response.data.status === "Success") {
        toast.toast({
          title: "Success",
          description: "Operator Domain updated successfully.",
        });
        await fetchDomains(); // Refresh domain list
      }else if (response.data.status === "Failure") {
              toast.toast({
          title: "Warning",
          description: "Operator Domain already exists.",
        });
        await fetchDomains(); // Refresh domain list
      }
       else {
        toast.toast({
          title: "Error",
          description: "Failed to update Operator Domain",
        });
      }
    } catch (error) {
      toast.toast({
        title: "Error",
        description: "Error occurred while update operator domain.",
      });
    } finally {
      setOpen(false); // Close dialog
      setDomainName(""); // Reset input field
      setDomainId(0);
      setIsLoading(false);
    }
  };

  const handleAddDomain = async () => {
    // Validate domain name
    if (!domainName) {
      setDomainError("Please enter a domain name.");
      return;
    }

    if (!domainRegex.test(domainName)) {
      setDomainError("Please enter a valid domain name.");
      return;
    }

    try {
      setIsLoading(true);
      
              const response = await axios.post(`${adminUrl}/AddOperatorEmailDomain`, {
        domainName: domainName,
        workspaceId: workspaceId,
        accountId: accountId
      });

      if (response.data.status === "Success") {
        toast.toast({
          title: "Success",
          description: "Operator Domain added successfully.",
        });
        await fetchDomains(); // Refresh domain list
      } else {
        toast.toast({
          title: "Error",
          description: "Failed to add Operator Domain",
        });
      }
    } catch (error) {
      toast.toast({
        title: "Error",
        description: "Error occurred while adding operator domain.",
      });
    } finally {
      setDomainName(""); // Reset input field
      setDomainId(0);
      setOpen(false); // Close dialog
      setIsLoading(false);
    }
  };

  const handleEdit = (domain_id: number, domain_name: string) => {
    setIsEditMode(true);
    console.log("Handle Edit :", domain_id, domain_name);
    setDomainName(domain_name);
    setDomainId(domain_id);
    setOpen(true);
  };

  const handleDelete = (domain_id: number, domain_name: string) => {
    setIsDeleteMode(true);
    console.log("Handle Delete :", domain_id, domain_name);
    setDomainName(domain_name);
    setDomainId(domain_id);
    setOpen(true);
  };

  return (
    <div className="flex-col gap-6 h-full overflow-y-auto no-scrollbar">
      <Toaster />

      {isLoading ? (
        <div className="flex flex-col items-center justify-center h-[500px]">
          <CircularProgress color="primary" />
        </div>
      ) : (
        <>
          <Card>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-lg font-bold text-left">
                    Operator Domains
                  </h2>
                  <p className="text-sm text-gray-600">
                    Manage the domains of operators.
                  </p>
                </div>
                <Button
                  className="mt-4 w-auto cursor-pointer"
                  onClick={() => setOpen(true)}
                >
                  + Add New Domain
                </Button>
              </div>
            </CardHeader>

            <CardContent>
              {domains.length > 0 ? (
                <div className="rounded-md border mt-2">
                  <div className="max-h-[50vh] overflow-y-auto">
                    <Table
                      className="rounded-xl whitespace-nowrap border-gray-200"
                      style={{ color: "#020202", fontSize: "15px" }}
                    >
                      <TableHeader className="text-center text-[14px] font-medium">
                        <TableRow className="sticky top-0 z-10 bg-white hover:bg-gray-100">
                          <TableHead className="text-left px-4 py-4 cursor-pointer">
                            S.No
                          </TableHead>
                          <TableHead className="text-left px-4 py-4 cursor-pointer">
                            Domain Name
                          </TableHead>
                          <TableHead className="text-left px-4 py-4 cursor-pointer">
                            Created Date
                          </TableHead>
                          <TableHead className="text-left px-4 py-4 cursor-pointer">
                            {" "}
                          </TableHead>
                        </TableRow>
                      </TableHeader>

                      <TableBody className="text-left text-[14px] font-normal text-[#020617]">
                        {domains.map((domain, index) => (
                          <TableRow
                            key={`${domain.domain_name}-${index}`}
                            className="hover:bg-gray-100"
                          >
                            <TableCell className="px-4 py-4">
                              {index + 1}
                            </TableCell>
                            <TableCell className="px-4 py-4">
                              {domain.domain_name}
                            </TableCell>
                            {/* <TableCell className="px-4 py-4">
              {formatDate(domain.created_date)}
            </TableCell> */}
                            <TableCell className="px-4 py-4">
                              {
                                convertUTCToLocal(
                                  domain.created_date,
                                  workspaceTimezone
                                ).localDate
                              }{" "}
                              ∙{" "}
                              {
                                convertUTCToLocal(
                                  domain.created_date,
                                  workspaceTimezone
                                ).localTime
                              }
                            </TableCell>
                            <TableCell className="px-4 py-4">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <DotsHorizontalIcon className="cursor-pointer w-6 h-6" />
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                  className="w-28 translate-x-4 rounded-lg shadow-md border border-gray-200"
                                  align="end"
                                >
                                  <DropdownMenuItem
                                    className="cursor-pointer"
                                    onClick={() =>
                                      handleEdit(
                                        domain.domain_id,
                                        domain.domain_name
                                      )
                                    }
                                  >
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="cursor-pointer"
                                    onClick={() =>
                                      handleDelete(
                                        domain.domain_id,
                                        domain.domain_name
                                      )
                                    }
                                  >
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>{" "}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-[100px]">
                  <h2 className="text-[24px] font-semibold mb-1 text-[#000000]">
                    No operator domains available.
                  </h2>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Add Domain Dialog */}
          <Dialog open={open} onOpenChange={handleCloseDialog}>
            <DialogContent className="w-full max-w-lg max-h-screen bg-white rounded-lg shadow-lg">
              <DialogHeader className="flex flex-col mb-1">
                <DialogTitle className="text-xl font-semibold">
                  {isDeleteMode
                    ? "Delete Domain"
                    : isEditMode
                    ? "Update Domain"
                    : "Add New Domain"}
                </DialogTitle>
                <DialogDescription className="text-sm text-gray-500">
                  {isDeleteMode
                    ? "Please confirm if you want to proceed with deleting this domain."
                    : isEditMode
                    ? "Please enter the domain for updation."
                    : "Please enter the domain of the new operator."}
                </DialogDescription>
              </DialogHeader>

              <div className="mt-1">
                <Label
                  htmlFor="domain"
                  className="block text-sm font-medium text-gray-700"
                >
                  Domain Name
                </Label>
                <Input
                  id="domain"
                  disabled={isDeleteMode}
                  value={domainName}
                  onChange={handleDomainChange}
                  placeholder="Enter operator domain"
                  className="mt-2 w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {domainError && (
                  <p className="text-red-500 text-xs mt-1">{domainError}</p>
                )}
              </div>

              <DialogFooter className="flex justify-between gap-2 -mt-3">
                <Button
                  variant="outline"
                  className="w-1/2 cursor-pointer"
                  onClick={handleCloseDialog}
                >
                  Cancel
                </Button>
                <Button
                  className="w-1/2 cursor-pointer"
                  disabled={!domainName || !!domainError}
                  onClick={handleSubmit}
                >
                  {isDeleteMode
                    ? "Confirm"
                    : isEditMode
                    ? "Update Domain"
                    : "Add Domain"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
};

export default OperatorDomains;
